// @ts-nocheck
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Hotel, Plus, Trash2, Pencil, Check } from 'lucide-react';
import { format, parseISO } from 'date-fns';

interface Props {
  eventId: string;
}

/**
 * Hotel partner assignment panel for the admin event detail page.
 * Ported from the TBD partner portal's TbdAssignmentPanel.
 */
const HotelAssignmentPanel: React.FC<Props> = ({ eventId }) => {
  const { user } = useAuth();
  const qc = useQueryClient();

  const [assigning, setAssigning] = useState(false);
  const [selectedPartnerId, setSelectedPartnerId] = useState('');
  const [bookingUrl, setBookingUrl] = useState('');
  const [rateExpiry, setRateExpiry] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editUrl, setEditUrl] = useState('');
  const [editExpiry, setEditExpiry] = useState('');

  // Assignments for this event
  const { data: assignments, isLoading } = useQuery({
    queryKey: ['hotel-event-assignments', eventId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('hotel_event_assignments')
        .select('id, hotel_partner_user_id, booking_url, group_rate_expiry, created_at')
        .eq('event_id', eventId);
      if (error) throw error;
      const rows = data || [];

      // Look up partner profiles (hotel_partner_user_id is the auth user id)
      const userIds = rows.map((r: any) => r.hotel_partner_user_id).filter(Boolean);
      let profileMap = new Map<string, any>();
      if (userIds.length > 0) {
        const { data: profs } = await (supabase as any)
          .from('profiles')
          .select('user_id, first_name, last_name, name, email')
          .in('user_id', userIds);
        profileMap = new Map((profs || []).map((p: any) => [p.user_id, p]));
      }

      return rows.map((r: any) => ({ ...r, partner: profileMap.get(r.hotel_partner_user_id) || null }));
    },
    enabled: !!eventId,
  });

  // Available hotel partners (user_roles role='hotel_partner')
  const { data: partners } = useQuery({
    queryKey: ['hotel-partner-options'],
    queryFn: async () => {
      const { data: roles, error } = await (supabase as any)
        .from('user_roles')
        .select('user_id')
        .eq('role', 'hotel_partner');
      if (error) throw error;
      const userIds = (roles || []).map((r: any) => r.user_id);
      if (userIds.length === 0) return [];
      const { data: profs, error: e2 } = await (supabase as any)
        .from('profiles')
        .select('user_id, first_name, last_name, name, email')
        .in('user_id', userIds);
      if (e2) throw e2;
      return profs || [];
    },
  });

  // Compliance for this event
  const { data: compliance } = useQuery({
    queryKey: ['hotel-event-compliance', eventId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('v_hotel_booking_compliance')
        .select('*')
        .eq('event_id', eventId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!eventId,
  });

  // Outreach count for this event
  const { data: outreachCount } = useQuery({
    queryKey: ['hotel-event-outreach-count', eventId],
    queryFn: async () => {
      const { count, error } = await (supabase as any)
        .from('hotel_outreach_log')
        .select('id', { count: 'exact', head: true })
        .eq('event_id', eventId);
      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!eventId,
  });

  const assignMutation = useMutation({
    mutationFn: async () => {
      const { error } = await (supabase as any)
        .from('hotel_event_assignments')
        .insert({
          event_id: eventId,
          hotel_partner_user_id: selectedPartnerId,
          booking_url: bookingUrl || null,
          group_rate_expiry: rateExpiry || null,
          assigned_by: user?.id,
        });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hotel-event-assignments', eventId] });
      toast.success('Hotel partner assigned');
      setAssigning(false);
      setSelectedPartnerId('');
      setBookingUrl('');
      setRateExpiry('');
    },
    onError: (err: any) => toast.error(err.message || 'Failed to assign partner'),
  });

  const removeMutation = useMutation({
    mutationFn: async (assignmentId: string) => {
      const { error } = await (supabase as any)
        .from('hotel_event_assignments')
        .delete()
        .eq('id', assignmentId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hotel-event-assignments', eventId] });
      toast.success('Assignment removed');
    },
    onError: (err: any) => toast.error(err.message || 'Failed to remove assignment'),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, url, expiry }: { id: string; url: string; expiry: string }) => {
      const { error } = await (supabase as any)
        .from('hotel_event_assignments')
        .update({ booking_url: url || null, group_rate_expiry: expiry || null })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hotel-event-assignments', eventId] });
      toast.success('Assignment updated');
      setEditingId(null);
    },
    onError: (err: any) => toast.error(err.message || 'Failed to update'),
  });

  const assignedPartnerIds = new Set((assignments || []).map((a: any) => a.hotel_partner_user_id));
  const availablePartners = (partners || []).filter((p: any) => !assignedPartnerIds.has(p.user_id));

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg flex items-center gap-2">
          <Hotel className="h-5 w-5" /> Hotel Partner
        </CardTitle>
        {!assigning && availablePartners.length > 0 && (
          <Button size="sm" variant="outline" onClick={() => setAssigning(true)}>
            <Plus className="h-4 w-4 mr-1" /> Assign Partner
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Compliance summary */}
        {compliance && (
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-muted-foreground">
            <span>Teams: <strong className="text-foreground">{compliance.total_teams}</strong></span>
            <span>Confirmed: <strong className="text-foreground">{compliance.confirmed_bookings}</strong></span>
            <span>Unbooked: <strong className="text-red-600">{compliance.unbooked_teams}</strong></span>
            <span>Compliance: <strong className="text-foreground">{compliance.compliance_pct}%</strong></span>
            {(outreachCount ?? 0) > 0 && (
              <span>Outreach emails: <strong className="text-foreground">{outreachCount}</strong></span>
            )}
          </div>
        )}

        {/* Assignment list */}
        {isLoading ? (
          <Skeleton className="h-12 w-full" />
        ) : !assignments || assignments.length === 0 ? (
          <p className="text-sm text-muted-foreground">No hotel partner assigned to this event.</p>
        ) : (
          <div className="space-y-2">
            {assignments.map((a: any) => (
              <div key={a.id} className="border rounded-md p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">
                      {a.partner
                        ? [a.partner.first_name, a.partner.last_name].filter(Boolean).join(' ') || a.partner.name || a.partner.email
                        : 'Unknown partner'}
                    </p>
                    {a.partner?.email && (
                      <p className="text-xs text-muted-foreground">{a.partner.email}</p>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditingId(a.id);
                        setEditUrl(a.booking_url || '');
                        setEditExpiry(a.group_rate_expiry || '');
                      }}
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => {
                        if (window.confirm('Remove this hotel partner assignment?')) {
                          removeMutation.mutate(a.id);
                        }
                      }}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>

                {editingId === a.id ? (
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="space-y-1">
                      <label className="text-xs text-muted-foreground">Booking URL</label>
                      <Input value={editUrl} onChange={(e) => setEditUrl(e.target.value)} className="h-8 w-56 text-xs" />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs text-muted-foreground">Rate Expiry</label>
                      <Input type="date" value={editExpiry} onChange={(e) => setEditExpiry(e.target.value)} className="h-8 w-36 text-xs" />
                    </div>
                    <Button size="sm" onClick={() => updateMutation.mutate({ id: a.id, url: editUrl, expiry: editExpiry })}>
                      <Check className="h-3 w-3 mr-1" /> Save
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>Cancel</Button>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span>
                      Booking URL: {a.booking_url ? (
                        <a href={a.booking_url} target="_blank" rel="noreferrer" className="text-primary underline">
                          {a.booking_url.length > 40 ? a.booking_url.slice(0, 40) + '…' : a.booking_url}
                        </a>
                      ) : 'Not set'}
                    </span>
                    <span>
                      Rate expiry: {a.group_rate_expiry ? format(parseISO(a.group_rate_expiry), 'MMM d, yyyy') : 'Not set'}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Assign form */}
        {assigning && (
          <div className="border rounded-md p-3 space-y-3 bg-muted/30">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Partner</label>
              <Select value={selectedPartnerId} onValueChange={setSelectedPartnerId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a hotel partner..." />
                </SelectTrigger>
                <SelectContent>
                  {availablePartners.map((p: any) => (
                    <SelectItem key={p.user_id} value={p.user_id}>
                      {[p.first_name, p.last_name].filter(Boolean).join(' ') || p.name || p.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Booking URL</label>
                <Input value={bookingUrl} onChange={(e) => setBookingUrl(e.target.value)} placeholder="https://..." />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Group Rate Expiry</label>
                <Input type="date" value={rateExpiry} onChange={(e) => setRateExpiry(e.target.value)} />
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => assignMutation.mutate()}
                disabled={!selectedPartnerId || assignMutation.isPending}
              >
                Assign
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setAssigning(false)}>Cancel</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default HotelAssignmentPanel;
